import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '../../components/Button';
import { DataTable } from '../../components/DataTable';
import { useApi } from '../../hooks/useApi';
import { listCoachProfiles, type CoachProfile } from '../../lib/api';
import { PS, T } from '../../lib/theme';
import { CoachProfileDialog } from './CoachProfileDialog';

export function CoachProfilesPage() {
  const loader = useCallback((signal: AbortSignal) => listCoachProfiles(signal), []);
  const profiles = useApi(loader);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CoachProfile | null>(null);
  const openDialog = useCallback((profile: CoachProfile | null) => { setEditing(profile); setDialogOpen(true); }, []);
  const columns = useMemo<ColumnDef<CoachProfile, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'published',
        header: 'Status',
        cell: ({ row }) => {
          const published = row.original.published;
          return (
            <span style={published ? publishedStyle : draftStyle}>
              {published ? 'Published' : 'Draft'}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Button type="button" variant="secondary" onClick={() => openDialog(row.original)}>
            Edit
          </Button>
        ),
      },
    ],
    [openDialog],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 980 }}>
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ ...T.sectionTitle, margin: 0 }}>Public coaches</h1>
          <p style={{ ...T.caption, color: PS.textSec, margin: '4px 0 0' }}>
            Marketing profiles for the public site. Lesson scheduling remains under Coach series.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button type="button" onClick={() => openDialog(null)}>New profile</Button>
          <Button variant="secondary" onClick={profiles.refetch} disabled={profiles.loading}>
            Refresh
          </Button>
        </div>
      </header>

      {profiles.loading && (
        <p role="status" style={{ ...T.caption, color: PS.textSec, margin: 0 }}>
          Loading public coaches…
        </p>
      )}

      {!profiles.loading && profiles.error && (
        <div role="alert" style={errorStyle}>
          <span>Could not load public coaches.</span>
          <Button variant="secondary" onClick={profiles.refetch}>
            Retry
          </Button>
        </div>
      )}

      {!profiles.loading && !profiles.error && (
        <DataTable
          columns={columns}
          data={profiles.data ?? []}
          emptyLabel="No public coach profiles yet."
        />
      )}

      <CoachProfileDialog
        open={dialogOpen}
        profile={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={() => {
          setDialogOpen(false);
          profiles.refetch();
        }}
      />
    </div>
  );
}

const statusStyle: CSSProperties = {
  display: 'inline-block',
  padding: '4px 8px',
  borderRadius: 3,
  ...T.caption,
  fontWeight: 700,
};

const publishedStyle: CSSProperties = {
  ...statusStyle,
  color: PS.success,
  background: PS.successSoft,
};

const draftStyle: CSSProperties = {
  ...statusStyle,
  color: PS.textSec,
  background: PS.surfaceDisabled,
};

const errorStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
  padding: 12,
  borderRadius: 3,
  border: `1px solid ${PS.errorBorder}`,
  background: PS.errorBg,
  color: PS.errorText,
  ...T.ui,
};
